import { useEffect, useState, useMemo } from "react";
import { api } from "../api/client";
import { Spinner } from "../components/UI";

export default function GremiumBerechtigungenPage() {
  const [gremien, setGremien] = useState([]);
  const [selectedGid, setSelectedGid] = useState(null);
  const [data, setData] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState("");
  const [savingCell, setSavingCell] = useState(null);

  useEffect(() => {
    api.getGremien("admin.berechtigungen")
      .then(g => {
        setGremien(g || []);
        if (g && g.length > 0) setSelectedGid(g[0].id);
      })
      .finally(() => setLoadingList(false));
  }, []);

  const loadMatrix = (gid) => {
    if (!gid) return;
    setData(null);
    api.gremiumMatrix(gid)
      .then(d => {
        if (!d || !Array.isArray(d.berechtigungen)) {
          console.error("Matrix-Response unerwartet:", d);
          return;
        }
        setData(d);
      })
      .catch(e => { console.error("Matrix laden:", e); alert("Matrix konnte nicht geladen werden: " + (e?.message || e)); });
  };

  useEffect(() => { loadMatrix(selectedGid); }, [selectedGid]);

  const togglePerm = async (gruppeId, berechtigungId, current) => {
    const key = `${gruppeId}-${berechtigungId}`;
    setSavingCell(key);
    try {
      await api.gremiumMatrixToggle(selectedGid, gruppeId, berechtigungId, !current);
      setData(d => {
        const nd = { ...d, matrix: { ...d.matrix } };
        nd.matrix[gruppeId] = { ...(nd.matrix[gruppeId] || {}), [berechtigungId]: !current };
        return nd;
      });
    } catch (e) { alert("Speichern fehlgeschlagen: " + (e?.message || e)); }
    finally { setSavingCell(null); }
  };

  const recomputeDefaults = async () => {
    if (!selectedGid) return;
    if (!window.confirm("Default-Permissions für dieses Gremium setzen? Bereits vorhandene Einträge bleiben unverändert.")) return;
    try {
      await api.gremiumRecomputeDefaults(selectedGid);
      loadMatrix(selectedGid);
    } catch (e) { alert("Fehler: " + (e?.message || e)); }
  };

  const filteredBer = useMemo(() => {
    if (!data || !Array.isArray(data.berechtigungen)) return [];
    if (!search.trim()) return data.berechtigungen;
    const s = search.toLowerCase();
    return data.berechtigungen.filter(b =>
      b.kategorie.toLowerCase().includes(s) || b.schluessel.toLowerCase().includes(s)
    );
  }, [data, search]);

  const grouped = useMemo(() => {
    const g = {};
    if (!Array.isArray(filteredBer)) return g;
    filteredBer.forEach(b => {
      if (!g[b.kategorie]) g[b.kategorie] = [];
      g[b.kategorie].push(b);
    });
    return g;
  }, [filteredBer]);

  if (loadingList) return <Spinner />;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="breadcrumb">Verwaltung</div>
          <h2>Gremium-Berechtigungen</h2>
        </div>
      </div>
      <div className="content">
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, color: "#666" }}>Gremium:</label>
          <select className="form-control" style={{ maxWidth: 320 }}
                  value={selectedGid || ""}
                  onChange={e => setSelectedGid(Number(e.target.value))}>
            {gremien.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <input className="form-control" style={{ maxWidth: 240 }}
                 placeholder="Filter Berechtigung…"
                 value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-secondary btn-sm" onClick={recomputeDefaults}>
            🔄 Defaults setzen
          </button>
        </div>

        {!data ? <Spinner /> : (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ minWidth: 700, fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9f9f9" }}>
                  <th style={{ width: 280 }}>Berechtigung</th>
                  {data.gruppen.map(g => (
                    <th key={g.id} style={{ textAlign: "center", padding: "8px 4px", minWidth: 110 }}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{g.name}</div>
                      <div style={{ fontSize: 10, color: "#999" }}>{g.source}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([kat, bers]) => (
                  <KatBlock key={kat} kat={kat} bers={bers}
                           gruppen={data.gruppen} matrix={data.matrix}
                           toggle={togglePerm} savingCell={savingCell} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 16, padding: 10, background: "#f5f5f5", borderRadius: 6, fontSize: 12, color: "#666" }}>
          <strong>Hinweis:</strong> Permissions gelten <em>nur für dieses Gremium</em>.
          Die gleiche Gruppe kann in anderen Gremien andere Rechte haben.
          <br/>
          „🔄 Defaults setzen" füllt für zugeordnete Gruppen die Standard-Rollen-Permissions (nur fehlende; vorhandene werden NICHT überschrieben).
        </div>
      </div>
    </div>
  );
}

function KatBlock({ kat, bers, gruppen, matrix, toggle, savingCell }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <tr style={{ background: "#f0f0f0", cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <td colSpan={gruppen.length + 1} style={{ padding: "6px 12px", fontWeight: 600, fontSize: 13 }}>
          {open ? "▾" : "▸"} {kat} ({bers.length})
        </td>
      </tr>
      {open && bers.map(b => (
        <tr key={b.id}>
          <td style={{ padding: "6px 12px" }}>{b.schluessel}</td>
          {gruppen.map(g => {
            const checked = matrix[g.id]?.[b.id] === true;
            const cellKey = `${g.id}-${b.id}`;
            const saving = savingCell === cellKey;
            return (
              <td key={g.id} style={{ textAlign: "center", padding: 4 }}>
                {saving ? <span style={{ fontSize: 11, color: "#999" }}>…</span> :
                  <span style={{ cursor: "pointer", fontSize: 18, color: checked ? "" : "#ccc" }}
                        onClick={() => toggle(g.id, b.id, checked)}>
                    {checked ? "✅" : "☐"}
                  </span>
                }
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
